/**
 * Authentication.tsx - Authentication View
 *
 * Renders the canonical lib-core auth suite (login / register / phone OTP /
 * forgot password) under the circle's branding (avatar + name). The suite is
 * react-intl based, so it is wrapped in lib-core's I18nProvider fed with the
 * current i18next locale (keeps EN/AR + RTL in sync with the chat language).
 *
 * The panel runs with syncToUrl={false}: its state must stay in memory
 * because the chat's HashRouter owns the location hash.
 */

import { useTranslation } from 'react-i18next'
import {
    useApi,
    I18nProvider,
    RegistrationContextProvider,
    LoginForm,
    RegistrationForm,
    PhoneOtpForm,
    ConfirmationCode,
    ForgotPassword,
    ChangePassword,
    useSetOnLoginAction,
    useSetOnLoginUrl,
} from 'core'
import { useCircleContext } from '@/contexts/CircleContext'
import { writeAuthToken } from '@/utilities/authToken'
import { AvatarImage } from '@/ui/components/AvatarImage'
import { SafariStorageAccessBanner } from '@/ui/components/SafariStorageAccessBanner'

export const Authentication = () => {
    const api = useApi()
    const { i18n } = useTranslation()
    const { circleName, circleImageUrl } = useCircleContext()

    // reload this chat URL (not navigate away) once logged in
    useSetOnLoginUrl()

    // Persist the bearer token before the reload — the chat runs cross-site
    // where the session cookie is blocked, so auth must survive via
    // localStorage (see utilities/authToken.ts).
    useSetOnLoginAction((user: any) => {
        if (user && user.token) {
            api.setAuthToken(user.token)
            writeAuthToken(user.token)
        }
    })

    return (
        <I18nProvider key={i18n.language} browserLanguage={i18n.language}>
            {/* explicit light background: the index.html app wrapper is
                dark:bg-black (OS-scheme keyed) while the chat theme is forced
                light — without this the auth screen sits on a black canvas */}
            <div className="w-full min-h-dvh overflow-y-auto py-8 px-2 bg-gray-50">
                <div className="flex flex-col items-center gap-3 mb-4">
                    <AvatarImage size="2xl" avatarFallbackName={circleName} avatarImageUrl={circleImageUrl} />
                    {circleName && (
                        <div className="text-lg font-semibold text-gray-800 text-center">{circleName}</div>
                    )}
                    <div className="w-full max-w-md">
                        <SafariStorageAccessBanner />
                    </div>
                </div>
                <RegistrationContextProvider syncToUrl={false}>
                    <LoginForm />
                    <RegistrationForm />
                    <PhoneOtpForm />
                    <ConfirmationCode />
                    <ForgotPassword />
                    <ChangePassword />
                </RegistrationContextProvider>
            </div>
        </I18nProvider>
    )
}
